import { useState } from "react";
import { Plus, Minus, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { rupiah, effectivePrice } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const CAT_PLACEHOLDER = {
  "Sembako": { emoji: "🍚", bg: "bg-amber-100" },
  "Minuman": { emoji: "🥤", bg: "bg-sky-100" },
  "Snack & Biskuit": { emoji: "🍪", bg: "bg-orange-100" },
  "Mie Instan": { emoji: "🍜", bg: "bg-yellow-100" },
  "Bumbu & Saus": { emoji: "🧂", bg: "bg-red-100" },
  "Kebersihan Rumah": { emoji: "🧼", bg: "bg-teal-100" },
  "Perawatan Diri": { emoji: "🧴", bg: "bg-pink-100" },
  "Ibu & Bayi": { emoji: "🍼", bg: "bg-violet-100" },
  "Perlengkapan Rumah": { emoji: "🏠", bg: "bg-lime-100" },
  "Obat & Kesehatan": { emoji: "💊", bg: "bg-rose-100" },
  "Alat Tulis & Kantor": { emoji: "✏️", bg: "bg-indigo-100" },
};

export default function ProductCard({ product }) {
  const { items, add, setQty } = useCart();
  const variants = product.variants || [];
  const hasVariants = variants.length > 0;
  const [variantIdx, setVariantIdx] = useState(0);
  const variant = hasVariants ? variants[variantIdx] : null;
  const variantName = variant ? variant.name : "";
  const price = variant ? variant.price : effectivePrice(product);
  const key = `${product.id}::${variantName}`;
  const inCart = items.find((i) => i.key === key);
  const qty = inCart?.qty || 0;

  const handleAdd = () => {
    add(product, 1, variantName, variant ? variant.price : null);
    toast.success(`+1 ${product.name}${variantName ? ` (${variantName})` : ""}`, { description: `Ditambah ke keranjang · ${rupiah(price)}` });
  };

  return (
    <motion.div
      layout
      whileHover={{ y: -4 }}
      className="group rounded-2xl bg-white border border-[#E5DECF] overflow-hidden flex flex-col"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative aspect-square overflow-hidden bg-white">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className={`w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 ${product.out_of_stock ? "grayscale opacity-60" : ""}`}
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center text-4xl ${(CAT_PLACEHOLDER[product.category] || {}).bg || "bg-stone-200"}`}>
            {(CAT_PLACEHOLDER[product.category] || {}).emoji || "🛒"}
          </div>
        )}
        {product.is_promo && (
          <span className="absolute top-2 left-2 tag-pill bg-orange-500 text-white text-[10px]">
            −{product.promo_percent}% Promo
          </span>
        )}
        {product.badge && (
          <span data-testid={`product-badge-${product.id}`} className="absolute top-2 right-2 tag-pill bg-stone-900/90 text-amber-300 text-[10px] backdrop-blur">
            {product.badge}
          </span>
        )}
        {product.lead_time && (
          <span data-testid={`product-lead-time-${product.id}`} className="absolute bottom-2 left-2 tag-pill bg-stone-900/80 text-white text-[10px] backdrop-blur">
            <Clock className="w-3 h-3" /> {product.lead_time}
          </span>
        )}
        {product.out_of_stock && (
          <span data-testid={`product-out-of-stock-badge-${product.id}`} className="absolute inset-0 bg-white/60 flex items-center justify-center">
            <span className="tag-pill bg-amber-500 text-white text-xs">Coming Soon</span>
          </span>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 font-semibold">{product.category}</div>
        <h3 className="mt-1 font-display font-bold text-stone-900 text-sm leading-tight line-clamp-2 min-h-[36px]">{product.name}</h3>

        {hasVariants && (
          <div className="mt-2 flex gap-1 flex-wrap" data-testid={`product-variants-${product.id}`}>
            {variants.map((v, i) => (
              <button
                key={v.name}
                data-testid={`product-variant-${product.id}-${v.name}`}
                onClick={() => setVariantIdx(i)}
                className={`px-2 py-1 rounded-lg text-[11px] font-bold border transition ${
                  i === variantIdx
                    ? "bg-stone-900 text-white border-stone-900"
                    : "bg-white text-stone-600 border-[#E5DECF] hover:border-emerald-700"
                }`}
              >
                {v.name}
              </button>
            ))}
          </div>
        )}

        <div className="mt-2 flex items-baseline gap-1.5">
          <span className="font-display font-black text-lg text-emerald-800">{rupiah(price)}</span>
          {product.is_promo && !variant && (
            <span className="text-xs text-stone-400 line-through">{rupiah(product.price)}</span>
          )}
          {product.original_price > price && (
            <span className="text-xs text-stone-400 line-through">{rupiah(product.original_price)}</span>
          )}
          <span className="text-[10px] text-stone-500 ml-auto">/ {product.unit}</span>
        </div>
        {product.sold && (
          <div data-testid={`product-sold-${product.id}`} className="mt-1 text-[10px] text-stone-500">Terjual <b className="text-emerald-700">{product.sold} pcs</b> · dipercaya pembeli Nusantara</div>
        )}
        <div className="mt-3">
          {product.out_of_stock ? (
            <button disabled data-testid={`product-out-of-stock-btn-${product.id}`} className="w-full py-2 rounded-full bg-emerald-700 text-white text-sm font-bold flex items-center justify-center gap-1 cursor-not-allowed">
              <Plus className="w-4 h-4" /> Tambah
            </button>
          ) : qty === 0 ? (
            <button
              onClick={handleAdd}
              data-testid={`product-add-to-cart-btn-${product.id}`}
              className="w-full py-2 rounded-full bg-emerald-700 text-white text-sm font-bold hover:bg-emerald-800 transition flex items-center justify-center gap-1"
            >
              <Plus className="w-4 h-4" /> Tambah
            </button>
          ) : (
            <div className="flex items-center justify-between rounded-full bg-emerald-700 text-white px-1 py-1">
              <button
                data-testid={`product-decrement-${product.id}`}
                onClick={() => setQty(key, qty - 1)}
                className="w-8 h-8 rounded-full hover:bg-emerald-800 flex items-center justify-center"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="font-bold text-sm" data-testid={`product-qty-${product.id}`}>{qty}</span>
              <button
                data-testid={`product-increment-${product.id}`}
                onClick={() => setQty(key, qty + 1)}
                className="w-8 h-8 rounded-full hover:bg-emerald-800 flex items-center justify-center"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
